import { apiPost, apiRequest } from "@/lib/api/client";
import Logger from "@/lib/utils/logger";

/**
 * Plain strings, not the literal union: an authored flow adds steps without
 * a deploy, and the API validates against what is published.
 */
export interface TourStepEventInput {
  fromStep: string | null;
  toStep: string;
  choice: string | null;
}

/**
 * Record one step of the setup screens or the onboarding tour, without ever
 * delaying the member.
 *
 * Deliberately not awaited by callers and deliberately silent on failure: a
 * dropped analytics row is worth far less than a tour that stalls between
 * steps. Landing attribution is posted the same way from
 * {@link recordLandingAttribution}.
 *
 * @param input - The transition the member just made
 */
export function recordTourStepEvent(input: TourStepEventInput): void {
  // `keepalive` lets the request outlive the page: the are-you-serious "no"
  // sends the member off-site a moment after this fires.
  void apiRequest<{ recorded: boolean }>("/api/v1/user/onboarding/step-event", {
    method: "POST",
    body: JSON.stringify(input),
    keepalive: true,
  }).catch((error) => {
    Logger.warn("[onboardingFunnel] Failed to record tour step event", error);
  });
}

export interface LandingAttributionInput {
  visitorId: string | null;
  referrerUrl: string | null;
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  utmContent: string | null;
  utmTerm: string | null;
  landingPath: string | null;
  sourcePage: string | null;
  sourceCta: string | null;
  sourceReferrer: string | null;
  landedAt: string;
  isMobileDevice: boolean;
}

/**
 * Copy the shared landing cookie into the member's record.
 *
 * The server fills only what it does not know yet, so a repeat call is safe.
 * Rejects with `ApiError` 404 while the member's account does not exist yet —
 * the caller decides whether to retry (TX-5059).
 *
 * @param input - Landing details read from the `.thesukha.co` cookie
 * @returns Whether this call created the member's row
 */
export function recordLandingAttribution(
  input: LandingAttributionInput
): Promise<{ recorded: boolean }> {
  return apiPost<{ recorded: boolean }>("/api/v1/user/landing-attribution", input);
}
